import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Checkbox,
  Link,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import {
  ArrowsClockwise as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  LinkBreak as DisconnectIcon,
  Plugs as ConnectIcon,
  WarningCircle as WarningIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  connectionApi,
  integrationApi,
  type DistributorConnection,
  type ShipTo,
} from '../../lib/distributorsApi';
import DistributorCredentialsForm from './DistributorCredentialsForm';
import { oauthErrorMessage } from './abcConstants';
import { extractApiError } from '../../utils/apiError';

interface Props {
  distributorId: number;
  /** Result of an OAuth return parsed by DistributorsApp (?connected= / ?error=). */
  oauthResult?: { connected: boolean; error?: string } | null;
  onResultConsumed?: () => void;
}

type AlertState = { type: 'success' | 'danger' | 'warning'; message: string } | null;

const extractError = extractApiError;

/**
 * Org-level distributor account connection (ABC Supply).
 *
 * Pattern port of ProfilePage's "Connected Email Accounts" card: connect via
 * backend-provided auth_url, status chip, refresh/test, confirm-to-disconnect.
 */
export default function DistributorConnectionCard({ distributorId, oauthResult, onResultConsumed }: Props) {
  const queryClient = useQueryClient();
  const [alertMessage, setAlertMessage] = useState<AlertState>(null);
  const [accepted, setAccepted] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const { data: integration } = useQuery({
    queryKey: ['distributor-integration', distributorId],
    queryFn: () => integrationApi.get(distributorId),
  });
  const vendor = integration?.display_name || 'Distributor';
  const isAbc = integration?.slug === 'abc_supply';
  const mockSignIn = isAbc && integration?.mock_mode === true;


  // Surface the OAuth return outcome once, then let the parent clear it.
  useEffect(() => {
    if (!oauthResult) return;
    if (oauthResult.connected) {
      setAlertMessage({ type: 'success', message: `${vendor} account connected.` });
    } else if (oauthResult.error) {
      setAlertMessage({ type: 'danger', message: oauthErrorMessage(oauthResult.error) });
    }
    queryClient.invalidateQueries({ queryKey: ['distributor-connection', distributorId] });
    onResultConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthResult]);

  const { data: connection, isLoading, error: connectionError } = useQuery<DistributorConnection>({
    queryKey: ['distributor-connection', distributorId],
    queryFn: () => connectionApi.get(distributorId),
  });

  const connectMutation = useMutation({
    mutationFn: () => connectionApi.startOAuth(distributorId, isAbc ? { accepted, terms_version: '2026-04-09', disclosure_version: '2026-10-29' } : undefined),
    onSuccess: (data) => {
      window.location.href = data.auth_url;
    },
    onError: (error) => {
      setAlertMessage({
        type: 'danger',
        message: extractError(error, `Could not start the ${vendor} connection.`),
      });
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => connectionApi.refreshAccounts(distributorId),
    onSuccess: () => {
      setAlertMessage({ type: 'success', message: 'Connection OK — Ship-To accounts refreshed.' });
      queryClient.invalidateQueries({ queryKey: ['distributor-connection', distributorId] });
    },
    onError: (error) => {
      setAlertMessage({
        type: 'danger',
        message: extractError(error, 'Refreshing accounts failed — the connection may need to be reconnected.'),
      });
      queryClient.invalidateQueries({ queryKey: ['distributor-connection', distributorId] });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => connectionApi.disconnect(distributorId),
    onSuccess: () => {
      setAlertMessage({ type: 'success', message: `${vendor} account disconnected.` });
      for (const key of ['catalog-search', 'catalog-item-image', 'catalog-page-prices']) {
        queryClient.removeQueries({ queryKey: [key, distributorId] });
      }
      setDisconnectModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['distributor-connection', distributorId] });
    },
    onError: (error) => {
      setAlertMessage({ type: 'danger', message: extractError(error, 'Disconnect failed.') });
    },
  });

  const defaultsMutation = useMutation({
    mutationFn: (data: { ship_to_number?: string; branch_number?: string }) =>
      connectionApi.setDefaults(distributorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distributor-connection', distributorId] });
    },
    onError: (error) => {
      setAlertMessage({ type: 'danger', message: extractError(error, 'Saving defaults failed.') });
    },
  });

  const shipTos: ShipTo[] = connection?.ship_tos || [];
  const defaultShipTo = useMemo(
    () => shipTos.find((shipTo) => shipTo.is_default) || shipTos[0] || null,
    [shipTos],
  );
  const branches = defaultShipTo?.branches || [];
  const effectiveBranch =
    connection?.default_branch_number
    || branches.find((branch) => branch.homeBranch)?.number
    || branches[0]?.number
    || '';

  const isConnected = connection?.status === 'active';
  const needsAttention = connection?.status === 'error';

  const handleShipToChange = (_event: unknown, value: string | null) => {
    if (!value) return;
    const shipTo = shipTos.find((entry) => entry.number === value);
    const homeBranch = shipTo?.branches.find((branch) => branch.homeBranch) || shipTo?.branches[0];
    defaultsMutation.mutate({
      ship_to_number: value,
      ...(homeBranch ? { branch_number: homeBranch.number } : {}),
    });
  };

  const handleBranchChange = (_event: unknown, value: string | null) => {
    if (!value) return;
    defaultsMutation.mutate({ branch_number: value });
  };

  return (
    <Card data-sentry-block variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography level="title-md">{vendor} Account</Typography>
          {isLoading ? (
            <CircularProgress size="sm" />
          ) : isConnected ? (
            <Chip color="success" variant="soft" startDecorator={<CheckCircleIcon />}>
              {integration?.mock_mode ? 'Demo ready' : 'Connected'}
            </Chip>
          ) : needsAttention ? (
            <Chip color="warning" variant="soft" startDecorator={<WarningIcon />}>
              Needs attention
            </Chip>
          ) : (
            <Chip color="neutral" variant="soft">Not connected</Chip>
          )}
        </Box>

        {integration?.mock_mode && integration?.catalog_source !== 'sandbox' && <Alert color="warning">Mock mode: prices and order confirmations are simulated. No order is sent to the distributor. {isAbc && "ABC sign-in is disabled in mock mode. Use the simulated demo below to browse sample data and build test orders. Real ABC sandbox data requires sandbox mode and an ABC sandbox login."}</Alert>}
        {integration?.mock_mode && integration?.catalog_source === 'sandbox' && <Alert color="warning">Sandbox catalog demo: products and branch locations come from ABC’s sandbox. The customer account, prices and orders are simulated. No ABC customer login is needed and no order is sent to ABC. Use Start/Reset simulated demo if the connection needs attention.</Alert>}
        {!integration?.mock_mode && integration?.orders_simulated && <Alert color="warning">Sandbox demo: accounts, branches, products and prices come from ABC’s sandbox. Orders are simulated and are not sent to ABC.</Alert>}
        {!integration?.mock_mode && integration?.api_environment === 'sandbox' && <Alert color="warning">ABC sandbox: this connection uses ABC’s test environment.</Alert>}
        {connectionError && <Alert color="warning">{extractError(connectionError, 'Unable to load the distributor connection.')}</Alert>}
        {alertMessage && (
          <Alert
            color={alertMessage.type}
            variant="soft"
            endDecorator={
              <Button size="sm" variant="plain" color={alertMessage.type} onClick={() => setAlertMessage(null)}>
                Dismiss
              </Button>
            }
          >
            {alertMessage.message}
          </Alert>
        )}

        {!isLoading && !isConnected && (
          <>
            {needsAttention && connection?.error_message && (
              <Alert color="warning" variant="soft">{connection.error_message}</Alert>
            )}

            {integration?.connect_mode === 'credentials' ? (
              <DistributorCredentialsForm distributorId={distributorId} displayName={vendor}
                accountNoun={integration.account_noun} fields={integration.connection_form}
                onConnected={() => {
                  setAlertMessage({ type: 'success', message: `${vendor} account connected.` });
                  queryClient.invalidateQueries({ queryKey: ['distributor-connection', distributorId] });
                }} />
            ) : integration?.connect_mode === 'oauth_redirect' ? <>
              <Typography level="body-sm">Connect your company's account to unlock customer-specific pricing and direct ordering.</Typography>
              {isAbc && <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>ABC Supply provides account, branch, product, pricing and order information. ApexOS sends order items, jobsite addresses and contact details to ABC when you submit an order. Saved order records are visible to authorized members of your organization. Disconnect removes saved account details and credentials; order history remains subject to the approved retention policy. You can export your distributor data below.</Typography>
                <Checkbox checked={accepted} onChange={event => setAccepted(event.target.checked)} label={<Typography level="body-sm">I am authorized to connect this account, authorize these data uses and sharing within my organization, and agree to the <Link href="https://www.abcsupply.com/legal/" target="_blank" rel="noopener noreferrer">ABC API Terms of Use</Link> and the <Link href="/privacy" target="_blank" rel="noopener noreferrer">ApexOS Privacy Policy</Link>. I have the rights and consents needed for information submitted to ABC.</Typography>} />
              </Box>}
              <Button disabled={mockSignIn || (isAbc && !accepted)} startDecorator={<ConnectIcon />} loading={connectMutation.isPending} onClick={() => connectMutation.mutate()}>
                {connection?.id ? 'Reconnect' : 'Connect'} {vendor}
              </Button>
              {mockSignIn && <Button disabled={!accepted} loading={connectMutation.isPending} onClick={() => connectMutation.mutate()}>
                {connection?.id ? 'Reset simulated demo' : 'Start simulated demo'}
              </Button>}
            </> : <CircularProgress size="sm" />}
          </>
        )}

        {isConnected && (
          <>
            <Typography level="body-sm" color="neutral">
              Connected{connection?.connected_by_name ? ` by ${connection.connected_by_name}` : ''}
              {connection?.last_refreshed_at
                ? ` · tokens refreshed ${new Date(connection.last_refreshed_at).toLocaleString()}`
                : ''}
            </Typography>

            {shipTos.length === 0 ? (
              <Alert color="warning" variant="soft">
                No orderable Ship-To accounts are saved. Refresh accounts to request them from the distributor. If the refresh succeeds with no accounts, ask the distributor to confirm that this login has active job accounts and branches in the selected environment.
              </Alert>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <FormControl>
                  <FormLabel>Default Ship-To account</FormLabel>
                  <Select
                    value={defaultShipTo?.number || ''}
                    onChange={handleShipToChange}
                    disabled={defaultsMutation.isPending}
                  >
                    {shipTos.map((shipTo) => (
                      <Option key={shipTo.number} value={shipTo.number}>
                        {shipTo.number} — {shipTo.name || 'Unnamed'}
                      </Option>
                    ))}
                  </Select>
                  <FormHelperText>Used for pricing and new orders by default.</FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel>Default branch</FormLabel>
                  <Select
                    value={effectiveBranch}
                    onChange={handleBranchChange}
                    disabled={defaultsMutation.isPending || branches.length === 0}
                  >
                    {branches.map((branch) => (
                      <Option key={branch.number} value={branch.number}>
                        {branch.name || `Branch ${branch.number}`}
                        {branch.homeBranch ? ' (home)' : ''}
                      </Option>
                    ))}
                  </Select>
                  {branches.length === 0 && (
                    <FormHelperText>
                      This Ship-To has no enrolled branches — contact {vendor}.
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            )}

            <Divider />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="sm"
                variant="outlined"
                startDecorator={<RefreshIcon />}
                loading={refreshMutation.isPending}
                onClick={() => refreshMutation.mutate()}
              >
                Refresh accounts
              </Button>
              <Button
                size="sm"
                variant="outlined"
                color="danger"
                startDecorator={<DisconnectIcon />}
                onClick={() => setDisconnectModalOpen(true)}
              >
                Disconnect
              </Button>
            </Box>
          </>
        )}
      </Stack>

      <Button size="sm" variant="plain" onClick={async () => {
        try {
          const blob = await connectionApi.exportData(distributorId);
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a'); link.href = url; link.download = `distributor-${distributorId}-data.json`; link.click();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (error) { setAlertMessage({ type: 'danger', message: extractError(error, 'Export failed.') }); }
      }}>Export distributor data</Button>
      <Modal open={disconnectModalOpen} onClose={() => setDisconnectModalOpen(false)}>
        <ModalDialog role="alertdialog" variant="outlined">
          <Typography level="title-lg">Disconnect {vendor}?</Typography>
          <Typography level="body-md" sx={{ mt: 1 }}>
            Live pricing and API ordering will stop working for this distributor until
            an admin reconnects the account. Existing orders keep their history.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="plain" color="neutral" onClick={() => setDisconnectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="danger"
              loading={disconnectMutation.isPending}
              onClick={() => disconnectMutation.mutate()}
            >
              Disconnect
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Card>
  );
}
